import {
  canCancelProcurement,
  getAdjacentProcurementState,
  getProcurementStateTimelineIndex,
  ProcurementState,
  procurementStateTimeline,
} from "@boark/shared/types/procurement";

import { useLocale } from "../stores/locale";
import { Button } from "./button";

export function ProcurementStateTimeline({
  state,
  disabled = false,
  disableNext = false,
  loading = false,
  onStateChange,
  stateLabel,
}: {
  state: ProcurementState;
  disabled?: boolean;
  disableNext?: boolean;
  loading?: boolean;
  onStateChange: (state: ProcurementState) => void;
  stateLabel: (state: ProcurementState) => string;
}) {
  const { locale: t } = useLocale();
  const isDisabled = disabled || loading;

  if (state === ProcurementState.CANCELED) {
    return (
      <div className="w-full min-w-[20rem]">
        <span className="block text-sm font-medium text-gray-500 mb-3">
          {t.procurement.state}
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">
            {stateLabel(ProcurementState.CANCELED)}
          </span>
          <Button
            disabled={isDisabled}
            loading={loading}
            onClick={() => onStateChange(ProcurementState.OPEN)}
          >
            {t.procurement.actionReopen}
          </Button>
        </div>
      </div>
    );
  }

  const currentIndex = getProcurementStateTimelineIndex(state);
  const previousState = getAdjacentProcurementState(state, "previous");
  const nextState = getAdjacentProcurementState(state, "next");
  const showCancel = canCancelProcurement(state);

  return (
    <div className="w-full min-w-[20rem]">
      <span className="block text-sm font-medium text-gray-500 mb-3">
        {t.procurement.state}
      </span>
      <div className="flex items-center gap-3">
        <Button
          disabled={!previousState || isDisabled}
          loading={loading}
          severity="secondary"
          size="sm"
          onClick={() => {
            if (previousState) {
              onStateChange(previousState);
            }
          }}
        >
          {t.procurement.statePrevious}
        </Button>
        <ol
          aria-label={t.procurement.state}
          className="flex flex-1 items-start list-none m-0 p-0"
        >
          {procurementStateTimeline.map((stepState, index) => {
            const isCurrent = index === currentIndex;
            const isPast = index < currentIndex;
            const isLast = index === procurementStateTimeline.length - 1;

            return (
              <li
                key={stepState}
                className="relative flex flex-1 flex-col items-center min-w-0"
              >
                {index > 0 && (
                  <span
                    aria-hidden
                    className={`absolute top-3 right-1/2 w-full h-0.5 -translate-y-1/2 ${
                      isPast || isCurrent ? "bg-blue-500" : "bg-gray-300"
                    }`}
                  />
                )}
                {!isLast && (
                  <span
                    aria-hidden
                    className={`absolute top-3 left-1/2 w-full h-0.5 -translate-y-1/2 ${
                      isPast ? "bg-blue-500" : "bg-gray-300"
                    }`}
                  />
                )}
                <span
                  aria-current={isCurrent ? "step" : undefined}
                  className={`relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${
                    isCurrent
                      ? "border-blue-600 bg-blue-600 ring-4 ring-blue-100"
                      : isPast
                        ? "border-blue-500 bg-blue-500"
                        : "border-gray-300 bg-white"
                  }`}
                >
                  <span className="sr-only">{stateLabel(stepState)}</span>
                </span>
                <span
                  className={`mt-2 w-full px-1 text-center text-xs leading-tight ${
                    isCurrent
                      ? "font-semibold text-blue-700"
                      : isPast
                        ? "text-gray-700"
                        : "text-gray-400"
                  }`}
                >
                  {stateLabel(stepState)}
                </span>
              </li>
            );
          })}
        </ol>
        <Button
          disabled={!nextState || isDisabled || disableNext}
          loading={loading}
          severity="secondary"
          size="sm"
          title={disableNext ? t.procurement.winnerSelect : undefined}
          onClick={() => {
            if (nextState) {
              onStateChange(nextState);
            }
          }}
        >
          {t.procurement.stateNext}
        </Button>
      </div>
      {showCancel && (
        <div className="mt-3 flex justify-end">
          <Button
            disabled={isDisabled}
            loading={loading}
            severity="danger"
            size="sm"
            onClick={() => onStateChange(ProcurementState.CANCELED)}
          >
            {t.procurement.actionCancel}
          </Button>
        </div>
      )}
    </div>
  );
}
